import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

/**
 * Public contact form — anyone (signed-in or not) can reach the platform
 * admin. Backend rate-limits by IP + has a hidden honeypot field to swallow
 * bot spam silently. On success, admin gets an email with reply_to pointing
 * at the sender so they can just hit Reply.
 */
export default function Contact() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    subject: "",
    message: "",
    website: "", // honeypot — must stay empty
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.message.trim().length < 10) {
      setErr("Please write at least a sentence or two so we can help.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not send your message. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-6 py-14" data-testid="contact-page">
      <Link
        to="/"
        className="text-xs uppercase tracking-widest inline-flex items-center gap-1.5 opacity-70 hover:opacity-100"
      >
        <ArrowLeft className="h-3 w-3" /> Back
      </Link>
      <div className="overline mt-6" style={{ color: "var(--terracotta)" }}>
        Get in touch
      </div>
      <h1 className="font-serif text-5xl mt-3">Contact the admin.</h1>
      <p className="mt-3 text-sm max-w-xl" style={{ color: "var(--ink-soft)" }}>
        Question about your application? Concern about a listing? Idea to make AddVendTour better?
        Send us a note — real human replies, usually within a day.
      </p>

      {sent ? (
        <div
          data-testid="contact-success"
          className="mt-10 rounded-lg border p-8 text-center"
          style={{
            backgroundColor: "rgba(111,125,85,0.08)",
            borderColor: "rgba(111,125,85,0.30)",
          }}
        >
          <CheckCircle2 className="h-10 w-10 mx-auto" style={{ color: "var(--moss)" }} />
          <div className="font-serif text-2xl mt-3">Message sent.</div>
          <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
            Thanks {form.name.split(" ")[0] || "there"} — we'll get back to you at{" "}
            <strong>{form.email}</strong> as soon as we can.
          </p>
          <Link
            to="/"
            data-testid="contact-back-home"
            className="mt-6 inline-block btn-outline px-5 py-2.5 rounded-full text-sm"
          >
            Back to AddVendTour
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-xs uppercase tracking-widest">Your name</Label>
              <Input
                data-testid="contact-name"
                value={form.name}
                onChange={upd("name")}
                required
                maxLength={120}
                autoComplete="name"
                className="mt-2"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Your email</Label>
              <Input
                data-testid="contact-email"
                type="email"
                value={form.email}
                onChange={upd("email")}
                required
                autoComplete="email"
                className="mt-2"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Subject</Label>
            <Input
              data-testid="contact-subject"
              value={form.subject}
              onChange={upd("subject")}
              required
              maxLength={200}
              placeholder="What's this about?"
              className="mt-2"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Your message</Label>
            <Textarea
              data-testid="contact-message"
              value={form.message}
              onChange={upd("message")}
              required
              minLength={10}
              maxLength={4000}
              rows={7}
              placeholder="Tell us what's on your mind…"
              className="mt-2"
            />
            <div className="mt-1 text-[11px] opacity-60">
              {form.message.length} / 4000
            </div>
          </div>
          {/* Honeypot — visible only to bots (screen readers get aria-hidden). */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
            <label>
              Website (leave blank)
              <input
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={upd("website")}
              />
            </label>
          </div>
          {err && (
            <div
              data-testid="contact-error"
              className="text-sm rounded-md p-3"
              style={{ backgroundColor: "rgba(212,88,58,0.10)", color: "var(--terracotta)" }}
            >
              {err}
            </div>
          )}
          <Button
            type="submit"
            data-testid="contact-submit"
            disabled={busy}
            className="btn-primary rounded-full inline-flex items-center gap-2 px-6 py-3"
          >
            <Mail className="h-4 w-4" />
            {busy ? "Sending…" : "Send message"}
          </Button>
        </form>
      )}
    </div>
  );
}
